import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Headset, Package, Wrench, Truck, Award, Languages, ShieldCheck, Users, BadgeCheck, Handshake, Lightbulb, Split, Eye, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

// Import images
import tftLogo from '@/assets/tft-logo.png';
import heroProcess from '@/assets/hero-process.jpg';
import timelineHistory from '@/assets/timeline-history.png';
import heroLab from '@/assets/hero-lab.jpg';
import heroLiquid from '@/assets/hero-liquid-packaging.jpg';
import heroSolid from '@/assets/hero-solid-packaging.jpg';
import heroSecondary from '@/assets/hero-secondary-packaging.jpg';
import heroPalletizing from '@/assets/hero-palletizing-realistic.jpg';
import heroServices from '@/assets/presentation/service-technician.jpg';
import heroTurnkey from '@/assets/hero-turnkey.jpg';
import milkCartons from '@/assets/dairy-products/milk-cartons.jpg';
import companyHero from '@/assets/company-hero.jpg';

// Industries
import dairyImg from '@/assets/dairy-products/milk-cartons.jpg';
import beverageImg from '@/assets/industries/beverage.jpg';
import bakeryImg from '@/assets/industries/bakery.jpg';
import meatFishImg from '@/assets/industries/meat-fish.jpg';
import oilsImg from '@/assets/industries/oils.jpg';
import spreadsImg from '@/assets/industries/spreads.jpg';
import fruitsVegImg from '@/assets/industries/fruits-vegetables.jpg';
import readyMealsImg from '@/assets/industries/ready-meals.jpg';

// Partners (shared, always up to date with the Partners page)
import { partnersData, getPartnersForIndustry } from '@/data/partners';

// Import industry content
import { industryContent } from '@/data/industryContent';

// Vision / Mission / Values poster artwork (from the TFT lightbox PDF)
import lightboxVision from '@/assets/presentation/lightbox-vision.jpg';
import lightboxMission from '@/assets/presentation/lightbox-mission.jpg';
import lightboxValues from '@/assets/presentation/lightbox-values.jpg';
import posterLogo from '@/assets/presentation/tft-poster-logo.png';

// Renders **word** segments as highlighted bold text.
const renderHighlighted = (text: string, className = 'font-bold text-white') =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className={className}>{part.slice(2, -2)}</strong>
      : part
  );

const Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const slides = [
    // Slide 1: Title
    {
      title: { en: 'TESCO FOOD TECHNOLOGY', el: 'TESCO FOOD TECHNOLOGY' },
      subtitle: { en: 'Together for Tomorrow', el: 'Together for Tomorrow' },
      tagline: { en: 'Integration • Innovation • Integrity', el: 'Integration • Innovation • Integrity' },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]',
      image: companyHero,
      showLogo: true
    },
    // Slide 2: Who We Are
    {
      title: { en: 'Who We Are', el: 'Ποιοι Είμαστε' },
      subtitle: { en: 'Technology. Collaboration. Results.', el: 'Τεχνολογία. Συνεργασία. Αποτέλεσμα.' },
      content: {
        en: 'We are a team of engineers designing and implementing process, packaging & automation solutions for the food and beverage industry. From concept to production start-up, we deliver efficiency, safety, and continuous improvement at every stage.',
        el: 'Είμαστε ομάδα μηχανικών που σχεδιάζει και υλοποιεί λύσεις process, packaging & automation για τη βιομηχανία τροφίμων και ποτών. Από τη μελέτη έως την έναρξη παραγωγής, φέρνουμε αποτελεσματικότητα, ασφάλεια και εξέλιξη σε κάθε στάδιο.'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#2563b5]',
      image: heroProcess
    },
    // Slide 3: Our History
    {
      title: { en: 'Our History', el: 'Η Πορεία μας' },
      subtitle: { en: '60+ Years of Excellence', el: '60+ Χρόνια Αριστείας' },
      content: {
        en: 'Since 1963, Tesco Food Technology has been pioneering food processing and packaging solutions, playing a key role in the growth and modernization of the Greek food and beverage industry. With over 60 years of technological excellence and trusted partnerships, we continue to evolve.',
        el: 'Από το 1963, η Tesco Food Technology πρωτοπορεί στις λύσεις επεξεργασίας και συσκευασίας τροφίμων, συμβάλλοντας καθοριστικά στην ανάπτυξη και τον εκσυγχρονισμό της ελληνικής βιομηχανίας τροφίμων και ποτών. Με περισσότερα από 60 χρόνια τεχνολογικής υπεροχής και αμοιβαίας εμπιστοσύνης, συνεχίζουμε να εξελισσόμαστε.'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#0F2B5B]',
      image: timelineHistory,
      isHistory: true
    },
    // Slides: Vision, Mission, Values — design, photos & text from the TFT lightbox posters PDF (**word** = bold)
    {
      title: { en: 'Our Vision', el: 'Το Όραμά μας' },
      poster: {
        tab: { en: 'OUR VISION', el: 'ΤΟ ΟΡΑΜΑ ΜΑΣ' },
        image: lightboxVision,
        position: 'center 60%',
        content: {
          en: 'To be **the most reliable partner** of the Greek Food & Beverage Industry, promoting **quality**, **innovation** and **sustainable growth**.',
          el: 'Να είμαστε **ο πιο αξιόπιστος συνεργάτης** της Ελληνικής Βιομηχανίας Τροφίμων & Ποτών, προωθώντας την **ποιότητα**, την **καινοτομία** και τη **βιώσιμη ανάπτυξη**.'
        }
      },
      background: 'bg-white'
    },
    {
      title: { en: 'Our Mission', el: 'Η Αποστολή μας' },
      poster: {
        tab: { en: 'OUR MISSION', el: 'Η ΑΠΟΣΤΟΛΗ ΜΑΣ' },
        image: lightboxMission,
        position: 'center 45%',
        content: {
          en: 'To offer complete solutions and specialised services that enhance the **efficiency**, **quality** and **reliability** of every production process.\n\nWith **know-how**, **consistency** and **dedication**, we stand by our customers at every step, contributing to their own success.',
          el: 'Να προσφέρουμε ολοκληρωμένες λύσεις και εξειδικευμένες υπηρεσίες που ενισχύουν την **αποδοτικότητα**, την **ποιότητα** και την **αξιοπιστία** κάθε παραγωγικής διαδικασίας.\n\nΜε **τεχνογνωσία**, **συνέπεια** και **αφοσίωση**, στεκόμαστε δίπλα στους πελάτες μας σε κάθε βήμα, συμβάλλοντας στη δική τους επιτυχία.'
        }
      },
      background: 'bg-white'
    },
    {
      title: { en: 'Our Values', el: 'Οι Αξίες μας' },
      poster: {
        tab: { en: 'OUR VALUES', el: 'ΟΙ ΑΞΙΕΣ ΜΑΣ' },
        image: lightboxValues,
        position: 'center 40%',
        values: [
          { icon: ShieldCheck, title: { en: 'INTEGRITY', el: 'ΑΚΕΡΑΙΟΤΗΤΑ' }, text: { en: 'We act with honesty, transparency and respect, building relationships of trust.', el: 'Ενεργούμε με ειλικρίνεια, διαφάνεια και σεβασμό, χτίζοντας σχέσεις εμπιστοσύνης.' } },
          { icon: Users, title: { en: 'CUSTOMER-CENTRICITY', el: 'ΠΕΛΑΤΟΚΕΝΤΡΙΚΟΤΗΤΑ' }, text: { en: 'We put the needs and innovation of our customers first, to build long-term partnerships.', el: 'Βάζουμε τις ανάγκες και την καινοτομία των πελατών μας πρώτες, για τη δημιουργία μακροχρόνιων συνεργασιών.' } },
          { icon: BadgeCheck, title: { en: 'RELIABILITY', el: 'ΑΞΙΟΠΙΣΤΙΑ' }, text: { en: 'We deliver consistent quality and dependable results that our customers and partners can rely on.', el: 'Παρέχουμε σταθερή ποιότητα και αξιόπιστα αποτελέσματα, στα οποία μπορούν να βασιστούν οι πελάτες και οι συνεργάτες μας.' } },
          { icon: Handshake, title: { en: 'COLLABORATION', el: 'ΣΥΝΕΡΓΑΣΙΑ' }, text: { en: 'We promote a culture of teamwork to achieve common goals within the company and with our partners.', el: 'Προωθούμε την κουλτούρα της ομαδικής εργασίας για την επίτευξη κοινών στόχων εντός εταιρείας και με τους συνεργάτες μας.' } },
          { icon: Lightbulb, title: { en: 'INNOVATION', el: 'ΚΑΙΝΟΤΟΜΙΑ' }, text: { en: 'We constantly seek new ideas, technologies and methods at the cutting edge, to deliver pioneering solutions.', el: 'Αναζητούμε συνεχώς νέες ιδέες, τεχνολογίες και μεθόδους, στην αιχμή των εξελίξεων, για να παρέχουμε πρωτοποριακές λύσεις.' } },
          { icon: Split, title: { en: 'FLEXIBILITY', el: 'ΕΥΕΛΙΞΙΑ' }, text: { en: 'We adapt quickly to changes in technology and markets to stay ahead of the competition.', el: 'Προσαρμοζόμαστε γρήγορα στις αλλαγές της τεχνολογίας και των αγορών για να παραμένουμε μπροστά από τον ανταγωνισμό.' } }
        ]
      },
      background: 'bg-white'
    },
    // Slide 6: Industries We Serve
    {
      title: { en: 'Industries We Serve', el: 'Κλάδοι Δραστηριότητας' },
      content: {
        en: 'Dairy • Beverages • Bakery, Grains & Snacks • Meat, Fish & Eggs • Oils & Fats • Spreads & Sauces • Fruit & Vegetables • Ready Meals',
        el: 'Γαλακτοκομικά • Ποτά • Αρτοσκευάσματα, Δημητριακά & Σνακ • Κρέας, Ψάρι & Αυγά • Έλαια & Λίπη • Αλείμματα & Σάλτσες • Φρούτα & Λαχανικά • Έτοιμα Γεύματα'
      },
      details: {
        en: 'Complete solutions for every stage of production with cutting-edge technology, reliability and high food safety standards.',
        el: 'Ολοκληρωμένες λύσεις για κάθε στάδιο παραγωγής με τεχνολογία αιχμής, αξιοπιστία και υψηλά πρότυπα ασφάλειας τροφίμων.'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#1a4280]',
      industryImages: [
        { img: dairyImg, label: { en: 'Dairy', el: 'Γαλακτοκομικά' } },
        { img: beverageImg, label: { en: 'Beverages', el: 'Ποτά' } },
        { img: bakeryImg, label: { en: 'Bakery', el: 'Αρτοσκευάσματα' } },
        { img: meatFishImg, label: { en: 'Meat & Fish', el: 'Κρέας & Ψάρι' } },
        { img: oilsImg, label: { en: 'Oils & Fats', el: 'Έλαια & Λίπη' } },
        { img: spreadsImg, label: { en: 'Spreads', el: 'Αλείμματα' } },
        { img: fruitsVegImg, label: { en: 'Fruits & Vegetables', el: 'Φρούτα & Λαχανικά' } },
        { img: readyMealsImg, label: { en: 'Ready Meals', el: 'Έτοιμα Γεύματα' } }
      ]
    },
    // Industry Slides - Dairy
    {
      title: { en: 'Dairy Solutions', el: 'Λύσεις Γαλακτοκομικών' },
      content: {
        en: industryContent.dairy.overview.en,
        el: industryContent.dairy.overview.el
      },
      industrySlug: 'dairy',
      details: {
        en: 'Complete solutions: Milk Processing • Yoghurt Production • Cheese Manufacturing • Butter & Ice Cream • Quality Control • End-of-Line Packaging',
        el: 'Ολοκληρωμένες λύσεις: Επεξεργασία Γάλακτος • Παραγωγή Γιαουρτιού • Κατασκευή Τυριού • Βούτυρο & Παγωτό • Ποιοτικός Έλεγχος • Τελική Συσκευασία'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] to-[#2563b5]',
      image: dairyImg
    },
    // Industry Slides - Beverages
    {
      title: { en: 'Beverage Solutions', el: 'Λύσεις Ποτών' },
      content: {
        en: industryContent.beverage.overview.en,
        el: industryContent.beverage.overview.el
      },
      industrySlug: 'beverage',
      details: {
        en: 'Water • Juices • Soft Drinks • Beer • Wine | Complete processing, filling and end-of-line solutions',
        el: 'Νερό • Χυμοί • Αναψυκτικά • Μπύρα • Κρασί | Πλήρεις λύσεις επεξεργασίας, πλήρωσης και τελικής συσκευασίας'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#0F2B5B]',
      image: beverageImg
    },
    // Industry Slides - Bakery
    {
      title: { en: 'Bakery, Grains & Snacks', el: 'Αρτοσκευάσματα, Δημητριακά & Σνακ' },
      content: {
        en: industryContent['bakery-grains-snacks'].overview.en,
        el: industryContent['bakery-grains-snacks'].overview.el
      },
      industrySlug: 'bakery-grains-snacks',
      details: {
        en: 'Complete chain: Dough Preparation • Baking • Packaging • Quality Control',
        el: 'Ολοκληρωμένη αλυσίδα: Προετοιμασία Ζύμης • Ψήσιμο • Συσκευασία • Ποιοτικός Έλεγχος'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#1a4280]',
      image: bakeryImg
    },
    // Industry Slides - Meat & Fish
    {
      title: { en: 'Meat, Fish & Eggs', el: 'Κρέας, Ψάρι & Αυγά' },
      content: {
        en: industryContent['meat-fish-eggs'].overview.en,
        el: industryContent['meat-fish-eggs'].overview.el
      },
      industrySlug: 'meat-fish-eggs',
      details: {
        en: 'Packaging solutions for meat, fish and liquid eggs • Quality control instruments',
        el: 'Λύσεις συσκευασίας για κρέας, ψάρια και υγρά αυγά • Όργανα ποιοτικού ελέγχου'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]',
      image: meatFishImg
    },
    // Industry Slides - Oils & Fats
    {
      title: { en: 'Oils & Fats', el: 'Έλαια & Λίπη' },
      content: {
        en: industryContent['oils-fats'].overview.en,
        el: industryContent['oils-fats'].overview.el
      },
      industrySlug: 'oils-fats',
      details: {
        en: 'Vegetable oils • Margarines • Specialty fats • Complete processing and packaging',
        el: 'Φυτικά έλαια • Μαργαρίνες • Ειδικά λίπη • Πλήρης επεξεργασία και συσκευασία'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#2563b5]',
      image: oilsImg
    },
    // Industry Slides - Spreads
    {
      title: { en: 'Spreads & Sauces', el: 'Αλείμματα & Σάλτσες' },
      content: {
        en: industryContent['spreads-sauces'].overview.en,
        el: industryContent['spreads-sauces'].overview.el
      },
      industrySlug: 'spreads-sauces',
      details: {
        en: 'Chocolate spreads • Peanut butter • Tahini • Sauces • Honey • Complete solutions',
        el: 'Επάλειμμα σοκολάτας • Φυστικοβούτυρο • Ταχίνι • Σάλτσες • Μέλι • Ολοκληρωμένες λύσεις'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#0F2B5B]',
      image: spreadsImg
    },
    // Industry Slides - Fruits & Vegetables
    {
      title: { en: 'Fruits & Vegetables', el: 'Φρούτα & Λαχανικά' },
      content: {
        en: industryContent['fruit-vegetables'].overview.en,
        el: industryContent['fruit-vegetables'].overview.el
      },
      industrySlug: 'fruit-vegetables',
      details: {
        en: 'Fresh cut • Frozen • Processing • Packaging • Quality control',
        el: 'Φρέσκα τεμαχισμένα • Κατεψυγμένα • Επεξεργασία • Συσκευασία • Ποιοτικός έλεγχος'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] to-[#1a4280]',
      image: fruitsVegImg
    },
    // Industry Slides - Ready Meals
    {
      title: { en: 'Ready Meals', el: 'Έτοιμα Γεύματα' },
      content: {
        en: industryContent['ready-meals'].overview.en,
        el: industryContent['ready-meals'].overview.el
      },
      industrySlug: 'ready-meals',
      details: {
        en: 'Complete solutions for ready meal production, packaging and preservation',
        el: 'Ολοκληρωμένες λύσεις για παραγωγή, συσκευασία και διατήρηση έτοιμων γευμάτων'
      },
      background: 'bg-gradient-to-br from-[#1a4280] via-[#2563b5] to-[#0F2B5B]',
      image: readyMealsImg
    },
    // Slide: Our Solutions
    {
      title: { en: 'Our Solutions', el: 'Οι Λύσεις μας' },
      content: {
        en: 'Process Equipment • Liquid Packaging • Solid Packaging • Secondary Packaging • Palletizing & Robotics • Lab & Quality Control • Services & Support',
        el: 'Εξοπλισμός Επεξεργασίας • Συσκευασία Υγρών • Συσκευασία Στερεών • Δευτερογενής Συσκευασία • Παλετοποίηση & Ρομποτική • Εργαστηριακός Έλεγχος • Υπηρεσίες & Υποστήριξη'
      },
      details: {
        en: 'From engineering to commissioning, we deliver turnkey solutions with 24/7 technical support.',
        el: 'Από το σχεδιασμό έως τη θέση σε λειτουργία, παρέχουμε ολοκληρωμένες λύσεις με τεχνική υποστήριξη 24/7.'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] to-[#2563b5]',
      image: heroTurnkey
    },
    // Slide: Major Projects & Clients
    {
      title: { en: 'Selected Projects', el: 'Επιλεγμένα Έργα' },
      subtitle: { en: 'Complete project scope:', el: 'Πλήρες αντικείμενο έργου:' },
      content: {
        en: 'Engineering • Equipment Supply • Installation • Commissioning • Training • After-Sales Support',
        el: 'Μηχανολογική Μελέτη • Προμήθεια Εξοπλισμού • Εγκατάσταση • Θέση σε Λειτουργία • Εκπαίδευση • Υποστήριξη'
      },
      clientLogos: true,
      background: 'bg-gradient-to-br from-[#1a4280] via-[#2563b5] to-[#0F2B5B]'
    },
    // Slide: Global Partners
    {
      title: { en: 'Global Partners', el: 'Παγκόσμιοι Συνεργάτες' },
      subtitle: {
        en: 'Strategic partnerships with world-leading manufacturers',
        el: 'Στρατηγικές συνεργασίες με κορυφαίους κατασκευαστές παγκοσμίως'
      },
      background: 'bg-gradient-to-br from-[#2563b5] to-[#0F2B5B]',
      partnerLogos: partnersData
    },
    // Slide: Services & Support
    {
      title: { en: 'Services & Support', el: 'Υπηρεσίες & Υποστήριξη' },
      content: {
        en: '24/7 Technical Support\nGenuine Spare Parts\nPreventive Maintenance\nFast Delivery\nCertified Technicians',
        el: 'Τηλεφωνική Υποστήριξη 24/7\nΓνήσια Ανταλλακτικά\nΠροληπτική Συντήρηση\nΤαχεία Παράδοση\nΠιστοποιημένοι Τεχνικοί'
      },
      details: {
        en: 'Complete technical support for all equipment with immediate response to every support request.',
        el: 'Πλήρης τεχνική υποστήριξη για όλο τον εξοπλισμό με άμεση ανταπόκριση σε κάθε αίτημα.'
      },
      background: 'bg-gradient-to-br from-[#0F2B5B] via-[#1a4280] to-[#2563b5]',
      image: heroServices,
      serviceIcons: true
    },
    // Slide: Quality & Certifications
    {
      title: { en: 'Quality & Certifications', el: 'Ποιότητα & Πιστοποιήσεις' },
      content: {
        en: 'ISO 9001:2015 Quality Management System\n\nOfficial representatives of leading equipment manufacturers\n\nRecognition for excellence from international organizations',
        el: 'ISO 9001:2015 Σύστημα Διαχείρισης Ποιότητας\n\nΕπίσημοι αντιπρόσωποι κορυφαίων κατασκευαστών εξοπλισμού\n\nΑναγνώριση αριστείας από διεθνείς οργανισμούς'
      },
      background: 'bg-gradient-to-br from-[#1a4280] to-[#0F2B5B]'
    },
    // Slide: Our Commitment
    {
      title: { en: 'Our Commitment', el: 'Η Δέσμευσή μας' },
      content: {
        en: 'We are committed to creating value through innovation, consistency and teamwork — with transparency and trust in every partnership, from design to long-term support.',
        el: 'Δεσμευόμαστε να δημιουργούμε αξία μέσα από καινοτομία, συνέπεια και συνεργασία — με διαφάνεια και εμπιστοσύνη σε κάθε συνεργασία, από τον σχεδιασμό έως τη μακροχρόνια υποστήριξη.'
      },
      subtitle: { en: 'Together for Tomorrow', el: 'Together for Tomorrow' },
      background: 'bg-gradient-to-br from-[#2563b5] via-[#0F2B5B] to-[#1a4280]'
    }
  ];

  const totalSlides = slides.length;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape') {
        navigate('/company');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);


  const currentSlideData = slides[currentSlide];
  const isLight = !!currentSlideData.poster;
  const controlClass = isLight
    ? 'text-[#17507d] hover:bg-[#17507d]/10'
    : 'text-white hover:bg-white/20';

  return (
    <>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">

        {/* Language Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-4 right-16 z-50 ${controlClass}`}
          onClick={() => {
            const newLang = language === 'en' ? 'el' : 'en';
            localStorage.setItem('language', newLang);
            window.location.reload();
          }}
          title={language === 'en' ? 'Αλλαγή σε Ελληνικά' : 'Switch to English'}
        >
          <div className="flex items-center gap-1">
            <Languages className="h-5 w-5" />
            <span className="text-sm font-bold">{language === 'en' ? 'EL' : 'EN'}</span>
          </div>
        </Button>

        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-4 right-4 z-50 ${controlClass}`}
          onClick={() => navigate('/company')}
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Slide Counter */}
        <div className={`absolute top-4 left-4 z-50 font-semibold ${isLight ? 'text-[#17507d]' : 'text-white'}`}>
          {currentSlide + 1} / {totalSlides}
        </div>


        {/* Slides for Screen - Current slide only */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            className={`w-full h-full flex items-center justify-center ${currentSlideData.background} relative overflow-hidden`}
        >
          {/* Background Image with Overlay */}
          {currentSlideData.image && (
            <div className="absolute inset-0">
              <img 
                src={currentSlideData.image} 
                alt={typeof currentSlideData.title === 'string' ? currentSlideData.title : (currentSlideData.title?.[language] || 'TFT Presentation')} 
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F2B5B]/80 via-[#1a4280]/80 to-[#2563b5]/80" />
            </div>
          )}

          <div className="relative z-10 max-w-7xl w-full px-8 md:px-16">
            {/* Industries with images */}
            {currentSlideData.industryImages ? (
              <div className="space-y-8">
                <div className="text-center space-y-4">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  <p className="text-base md:text-xl text-white/70 leading-relaxed max-w-3xl mx-auto">
                    {currentSlideData.details[language]}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {currentSlideData.industryImages.map((industry: any, idx: number) => (
                    <div key={idx} className="group relative overflow-hidden rounded-lg border-2 border-white/20 hover:border-white/40 transition-all">
                      <img 
                        src={industry.img}
                        alt={industry.label[language]}
                        className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end justify-center pb-2">
                        <span className="text-white font-semibold text-sm">{industry.label[language]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : currentSlideData.partnerLogos ? (
              /* Partners with logos */
              <div className="space-y-8">
                <div className="text-center space-y-4">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  {currentSlideData.subtitle && (
                    <p className="text-xl md:text-2xl text-white/90">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 justify-items-center">
                  {currentSlideData.partnerLogos.map((partner: any, idx: number) => (
                    <motion.div
                      key={partner.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(idx * 0.03, 0.9), duration: 0.3 }}
                      className="bg-white/95 rounded-md p-2 flex items-center justify-center hover:bg-white transition-all hover:scale-105 shadow-lg w-full h-16"
                      title={partner.name}
                    >
                      <img 
                        src={partner.logo}
                        alt={partner.name}
                        className="max-h-10 w-auto max-w-full object-contain"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : currentSlideData.isHistory ? (
              /* History slide with large image at bottom */
              <div className="space-y-8">
                <div className="text-center space-y-6 md:space-y-8 max-w-5xl mx-auto">
                  <h1 className={`${currentSlideData.industrySlug ? 'text-3xl md:text-5xl' : 'text-4xl md:text-6xl lg:text-7xl'} font-bold text-white`}>
                    {currentSlideData.title[language]}
                  </h1>
                  
                  {currentSlideData.subtitle && (
                    <p className="text-xl md:text-3xl text-white/90">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}

                  {currentSlideData.content && (
                    <p className="text-lg md:text-2xl text-white/80 leading-relaxed whitespace-pre-line">
                      {currentSlideData.content[language]}
                    </p>
                  )}
                </div>

                {currentSlideData.image && (
                  <div className="mt-8">
                    <img 
                      src={currentSlideData.image} 
                      alt={currentSlideData.title[language]}
                      className="rounded-lg shadow-2xl border-4 border-white/20 w-full h-auto max-h-[600px] object-contain mx-auto"
                    />
                  </div>
                )}
              </div>
            ) : currentSlideData.poster ? (
              /* Vision / Mission / Values — lightbox poster design */
              <div className="flex items-stretch gap-10 lg:gap-16 h-[86vh] max-w-6xl mx-auto">
                {/* Text column */}
                <div className="flex-1 flex flex-col py-2 min-w-0">
                  <motion.img
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    src={posterLogo}
                    alt="Tesco Food Technology"
                    className="h-16 md:h-20 lg:h-24 w-auto self-start object-contain"
                  />
                  <div className="flex-1 flex flex-col justify-center">
                    {/* Mobile title (tab is hidden on small screens) */}
                    <h2 className="md:hidden text-2xl font-extrabold text-[#17507d] mb-4">
                      {currentSlideData.poster.tab[language]}
                    </h2>
                    {currentSlideData.poster.values ? (
                      <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                        {currentSlideData.poster.values.map((value: any, idx: number) => {
                          const Icon = value.icon;
                          return (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, y: 16 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.15 + idx * 0.08, duration: 0.4 }}
                            >
                              <Icon className="h-7 w-7 text-[#17507d] mb-1.5" strokeWidth={2.2} />
                              <h3 className="text-base lg:text-lg font-extrabold text-[#17507d] tracking-wide">
                                {value.title[language]}
                              </h3>
                              <p className="text-sm lg:text-[0.95rem] text-[#17507d]/85 leading-snug">
                                {value.text[language]}
                              </p>
                            </motion.div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-5 max-w-xl">
                        {currentSlideData.poster.content[language].split('\n\n').map((para: string, idx: number) => (
                          <motion.p
                            key={idx}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 + idx * 0.15, duration: 0.45 }}
                            className="text-xl md:text-2xl lg:text-3xl text-[#17507d] leading-relaxed"
                          >
                            {renderHighlighted(para, 'font-extrabold text-[#17507d]')}
                          </motion.p>
                        ))}
                        <motion.div
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ delay: 0.5, duration: 0.4 }}
                          className="h-1 w-20 bg-[#17507d] origin-left mt-6"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Photo column with vertical blue tab */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="relative hidden md:block w-[34%] flex-shrink-0"
                >
                  <div className="absolute inset-0 overflow-hidden rounded-tr-[9rem]">
                    <img
                      src={currentSlideData.poster.image}
                      alt={currentSlideData.poster.tab[language]}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: currentSlideData.poster.position || 'center' }}
                    />
                  </div>
                  <div className="absolute left-0 top-[30%] -translate-x-1/2 bg-[#17507d] px-4 py-10 shadow-lg">
                    <span
                      className="block text-white font-extrabold text-2xl lg:text-4xl tracking-wide whitespace-nowrap"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                      {currentSlideData.poster.tab[language]}
                    </span>
                  </div>
                </motion.div>
              </div>
            ) : currentSlideData.clientLogos ? (
              /* Selected Projects with client logos */
              <div className="space-y-8 max-w-7xl mx-auto">
                <div className="text-center space-y-6 mb-8">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white">
                    {currentSlideData.title[language]}
                  </h1>
                  {currentSlideData.subtitle && (
                    <p className="text-lg md:text-2xl text-white/70 font-light">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}
                  {currentSlideData.content && (
                    <p className="text-xl md:text-3xl text-white font-semibold leading-relaxed">
                      {currentSlideData.content[language]}
                    </p>
                  )}
                </div>

                {/* Client Logos Grid */}
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                  {[
                    'Δέλτα', 'ΕΒΓΑ', 'Nestlé', 'Δωδώνη',
                    'ΦΑΓΕ', 'ΜΕΒΓΑΛ', 'ΚΡΙ ΚΡΙ', 'ΝΕΟΓΑΛ',
                    'ΗΠΕΙΡΟΣ', 'Friesland Campina', 'ΒΙΒΛΙΑ ΧΩΡΑ', 'ΓΕΡΟΒΑΣΙΛΕΙΟΥ',
                    'Κτήμα ΑΛΦΑ', 'ΣΚΟΥΡΑΣ', 'ΘΕΟΝΗ', 'ΞΥΝΟ ΝΕΡΟ',
                    'CANDIA', 'SEPTEM', 'ΠΑΠΑΔΟΠΟΥΛΟΥ', 'MONDELEZ',
                    'FLORA', 'ΠΕΤΤΑΣ', 'SOYA HELLAS', 'EUROCATERING', 'METLEN'
                  ].map((client, index) => (
                    <div
                      key={index}
                      className="bg-white/95 backdrop-blur-sm rounded-xl p-4 md:p-6 flex items-center justify-center hover:bg-white hover:scale-105 transition-all duration-300 shadow-lg border-2 border-white/30 min-h-[80px]"
                    >
                      <span className="text-base md:text-lg font-bold text-primary text-center">
                        {client}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Regular slides */
              <div className={`${currentSlideData.image ? 'grid md:grid-cols-2 gap-8 items-center' : 'text-center'}`}>
                <div className={`${currentSlideData.industrySlug ? 'space-y-4' : 'space-y-6 md:space-y-8'} ${!currentSlideData.image ? 'max-w-5xl mx-auto' : ''}`}>
                  {currentSlideData.showLogo && (
                    <div className="flex justify-start mb-12">
                      <div className="bg-white/50 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-white/40">
                        <img 
                          src={tftLogo} 
                          alt="Tesco Food Technology"
                          className="h-32 md:h-40 lg:h-48 w-auto object-contain"
                        />
                      </div>
                    </div>
                  )}
                  
                  <h1 className={`${currentSlideData.industrySlug ? 'text-3xl md:text-5xl' : 'text-4xl md:text-6xl lg:text-7xl'} font-bold text-white`}>
                    {currentSlideData.title[language]}
                  </h1>
                  
                  {currentSlideData.subtitle && (
                    <p className="text-xl md:text-3xl text-white/90">
                      {currentSlideData.subtitle[language]}
                    </p>
                  )}

                  {currentSlideData.tagline && (
                    <p className="text-2xl md:text-4xl text-white/95 font-medium tracking-wide">
                      {currentSlideData.tagline[language]}
                    </p>
                  )}

                  {currentSlideData.content && (
                    currentSlideData.serviceIcons ? (
                      /* Services with staggered animation and icons */
                      <div className="space-y-3">
                        {(() => {
                          const services = currentSlideData.content[language].split('\n');
                          const icons = [Headset, Package, Wrench, Truck, Award];
                          return services.map((service: string, idx: number) => {
                            const IconComponent = icons[idx];
                            return (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.15, duration: 0.5 }}
                                className="flex items-center gap-4 text-lg md:text-2xl text-white/90 font-medium bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 hover:bg-white/10 hover:border-white/30 transition-all"
                              >
                                <IconComponent className="w-6 h-6 md:w-8 md:h-8 text-white flex-shrink-0" />
                                <span>{service}</span>
                              </motion.div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className={`${currentSlideData.industrySlug ? 'text-base md:text-lg' : 'text-lg md:text-2xl'} text-white/80 leading-relaxed whitespace-pre-line`}>
                        {currentSlideData.content[language]}
                      </p>
                    )
                  )}

                  {currentSlideData.details && (
                    <p className={`${currentSlideData.industrySlug ? 'text-sm md:text-base' : 'text-base md:text-xl'} text-white/70 leading-relaxed`}>
                      {currentSlideData.details[language]}
                    </p>
                  )}

                </div>

                {currentSlideData.image && (
                  <div className="hidden md:flex flex-col gap-5">
                    <img 
                      src={currentSlideData.image} 
                      alt={currentSlideData.title[language]}
                      className={`rounded-lg shadow-2xl border-4 border-white/20 w-full h-auto object-cover ${currentSlideData.industrySlug ? 'max-h-[260px]' : 'max-h-[500px]'}`}
                    />
                    {currentSlideData.industrySlug && (() => {
                        const sectorPartners = getPartnersForIndustry(currentSlideData.industrySlug);
                        if (sectorPartners.length === 0) return null;
                        return (
                          <div className="space-y-2">
                            <p className="text-sm uppercase tracking-wider text-white/60 font-semibold">
                              {t('Συνεργάτες στον κλάδο', 'Partners in this industry')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {sectorPartners.map((partner, idx) => (
                                <motion.div
                                  key={partner.name}
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 0.2 + idx * 0.04, duration: 0.3 }}
                                  className="bg-white/95 rounded-md px-2 py-1.5 h-12 w-24 flex items-center justify-center shadow"
                                  title={`${partner.name} — ${partner.description[language]}`}
                                >
                                  <img src={partner.logo} alt={partner.name} className="max-h-9 max-w-full object-contain" />
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                )}
              </div>
            )}
          </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute left-4 top-1/2 -translate-y-1/2 z-50 ${controlClass} h-12 w-12 no-print`}
          onClick={prevSlide}
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 ${controlClass} h-12 w-12 no-print`}
          onClick={nextSlide}
        >
          <ChevronRight className="h-8 w-8" />
        </Button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 flex gap-2 no-print">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? (isLight ? 'w-8 bg-[#17507d]' : 'w-8 bg-white')
                  : (isLight ? 'w-2 bg-[#17507d]/30 hover:bg-[#17507d]/60' : 'w-2 bg-white/50 hover:bg-white/70')
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Presentation;
